import { connect } from 'react-redux';
import React, { useCallback, useEffect, useMemo, memo, useRef } from 'react';
import { Typography, Container, Box } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import PreviewDisplay from './Preview/PreviewDisplay';
import { detectSaveKeyPress, detectPreviewBackPress, detectPreviewForwardPress, detectGlobalPausePlayPreviewPress, detectUndoPress, detectRedoPress } from '../../../utilities/eventUtilities';
import CreatePageConstants from './CreatePageConstants';
import PresetDialog from './PresetPicker/PresetDialog';
import SettingsPanel from './Settings/SettingsPanel';
import Joyride from '../../misc/Joyride/Joyride';
import ExportDialog from '../../dialogs/ExportDialog';
import ConfirmationDialog from '../../dialogs/ConfirmationDialog';
import { createActions } from './store';
import BottomBarActions from './Toolbar/BottomBarActions';
import { executeCarefully } from '../../../utilities/basicUtilities';
import TopBar from './Toolbar/TopBar';
import Prompt from '../../misc/Prompt';
import AudioElement from './components/PlaybackControls/AudioElement';
import { updateDisplayLoaderPercentage } from './Preview/PreviewDisplayLoaders';
import { useNavigate, useParams } from 'react-router';
import { DEFAULT_VIDEO_SAVING_STATE } from './store/reducer';
import { directUploadTracker } from './store/extensions';

const settingsWidth = 450;

const styles = theme => ({
    main: {
        position: 'absolute',
        top: 0, bottom: 0, right: 0, left: 0
    },
    settingsContainer: {
        position: 'absolute',
        top: theme.spacing(8),
        bottom: theme.spacing(8),
        left: 0,
        width: settingsWidth,
        padding: 0
    },
    previewDisplayContainer: {
        height: 'calc(100% - ' + 129 + 'px)',
        width: 'calc(100% - ' + 450 + 'px)',
        maxWidth: 'none',
        padding: 0,
        marginRight: 0,
        position: 'relative'
    },
    fileControlsItem: {
        flex: '0 0 auto'
    },
    buttonContainerGrid: {
        height: '100%',
        marginTop: 0,
        flexWrap: 'nowrap'
    }
});

const CreatePage = (props) => {
    const {
        classes, videoDirty, audioDirty, showWalkthrough, showUpdateDialog,
        displayFileName, setProperty, applyAudioSettingsForPresetMode, getVideoSettings,
        getPresets, presetsLength, maxValues, getMaxValues, openExportDialog, setShowUpdateDialog,
        movePlayingPreviewBack, movePlayingPreviewForward, togglePlayingPreview, undo, redo,
        videoSettingsId, savingInProgress, saveVideoProject,
        lyricExtractionProcessing, lyricExtractionJobId,
        cancelLyricExtractionJob
    } = props;

    const params = useParams();
    const navigate = useNavigate();

    const lyricExtractionProcessingRef = useRef(false);
    const lyricExtractionJobIdRef = useRef(null);
    const cancelLyricExtractionJobFireAndForgetRef = useRef(null);

    useEffect(() => {
        lyricExtractionProcessingRef.current = lyricExtractionProcessing;
        lyricExtractionJobIdRef.current = lyricExtractionJobId;
    }, [lyricExtractionProcessing, lyricExtractionJobId]);

    useEffect(() => {
        cancelLyricExtractionJobFireAndForgetRef.current = cancelLyricExtractionJob;
    }, [cancelLyricExtractionJob]);

    const onJoyrideFinish = useCallback(() => {
        setProperty('showWalkthrough', false);
    }, [setProperty]);

    const onConfirmationDialogClose = useCallback(() => {
        setShowUpdateDialog(false);
    }, [setShowUpdateDialog]);

    const save = useCallback((showSnack, onSaveComplete) => {
        if (savingInProgress)
            return;

        saveVideoProject((videoId) => executeCarefully(onSaveComplete, videoId), showSnack, navigate);
    }, [savingInProgress, saveVideoProject, navigate]);

    const onExportDialogComplete = useCallback(() => {
        navigate(`/create/${videoSettingsId}`, { replace: true });
        navigate(`/exports/${videoSettingsId}`);
    }, [navigate, videoSettingsId]);

    const onExportDialogSeePlansClick = useCallback(() => {
        save(false, (videoSettingsId) => {
            navigate(`/create/${videoSettingsId}`, { replace: true });
            navigate(`/plans/c/${videoSettingsId}`);
        });
    }, [save, navigate]);

    const onDocumentKeyDown = (event) => {
        // Shortcuts to save the settings (Ctrl + S)
        detectSaveKeyPress(event, () => save(true));

        // Shortcuts to move the preview position
        detectPreviewBackPress(event, () => {
            event.preventDefault();
            setProperty('previewPositionDragging', true);
            movePlayingPreviewBack(1);
        });

        detectPreviewForwardPress(event, () => {
            event.preventDefault();
            setProperty('previewPositionDragging', true);
            movePlayingPreviewForward(1);
        });

        // Shortcuts to play the preview with space button when we're not using any control 
        if (document.activeElement && document.activeElement.tagName === 'BODY') {
            detectGlobalPausePlayPreviewPress(event, () => {
                togglePlayingPreview();
                event.preventDefault();
            });
        }

        // Shortcuts to use actions history
        detectUndoPress(event, undo);
        detectRedoPress(event, redo);
    };

    const onDocumentKeyUp = (event) => {
        detectPreviewBackPress(event, () => {
            setProperty('previewPositionDragging', false);
        });

        detectPreviewForwardPress(event, () => {
            setProperty('previewPositionDragging', false);
        });
    };

    const addMutationObserver = useCallback(() => {
        new MutationObserver((mutations, self) => {
            mutations.forEach(() => {
                if (!window.location?.href?.includes("create")) {
                    document.body.style.overflowY = '';
                    self.disconnect();
                    return;
                }

                if (document.body.style.overflowY !== 'hidden') {
                    document.body.style.overflowY = 'hidden';
                }
            })
        }).observe(document.body, { attributes: true, attributeFilter: ['style'] })
    }, []);

    // ComponentDidMount effect
    useEffect(() => {
        const tryCancelLyricExtractionJob = () => {
            try {
                const isProcessing = lyricExtractionProcessingRef.current;
                const jobId = lyricExtractionJobIdRef.current;
                if (!isProcessing || !jobId)
                    return;

                const cancelFn = cancelLyricExtractionJobFireAndForgetRef.current;
                if (typeof cancelFn === 'function')
                    cancelFn(jobId);
            } catch {
                // Ignore cancellation errors on unload
            }
        };

        const onBeforeUnload = () => {
            tryCancelLyricExtractionJob();
        };

        window.createPageAbortController = new AbortController();
        directUploadTracker.reset();
        window.addEventListener('beforeunload', onBeforeUnload);

        if (!params.id)
            setProperty('presetDialogOpen', true);

        if (!maxValues.fetched)
            getMaxValues();

        if (params.mod === CreatePageConstants.CREATE_MOD_PRESET) {
            getPresets(params.id);
            applyAudioSettingsForPresetMode();
            navigate('/create', { replace: true });
        } else {
            if (presetsLength < 1)
                getPresets();

            getVideoSettings(params.id, () => {
                if (params.mod === CreatePageConstants.CREATE_MOD_EXPORT && params.id) {
                    openExportDialog();
                    navigate(`/create/${params.id}`, { replace: true });
                }
            });
        }

        // The CSS property overflowY fixes the appearance of page scroll, but there are elements that override the styles,
        // so we need to catch this and add the CSS property back to the body element with the help of MutationObserver
        document.body.style.overflowY = 'hidden';
        addMutationObserver();

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            tryCancelLyricExtractionJob();

            window.createPageAbortController.abort();

            // Stop and reset the maximum magnitudes analyzer loader.
            updateDisplayLoaderPercentage(undefined);
            document.body.style.overflowY = '';

            setProperty('audioDuration', 0);
            setProperty('appliedPlaybackTime', 0);
            setProperty('audioAnalyzing', false);
            setProperty('exportDialogOpen', false);
            setProperty('backgroundDimensions', undefined);
            setProperty('currentSettingsPanel', CreatePageConstants.SETTINGS_PANELS.GENERAL);

            // Reset lyrics extraction state to prevent stale state when returning.
            setProperty('lyricExtractionProcessing', false);
            setProperty('lyricExtractionProgress', null);
            setProperty('lyricExtractionJobId', null);

            // Reset the video saving state.
            setProperty('videoSavingState', {
                ...DEFAULT_VIDEO_SAVING_STATE,
                savingPanelOpen: false,
                savingInProgress: false,
            });

            if (videoDirty || audioDirty)
                getVideoSettings(videoSettingsId);
        };
    }, []);

    useEffect(() => {
        // Add event listeners to handle the hot keys
        document.addEventListener('keydown', onDocumentKeyDown, false);
        document.addEventListener('keyup', onDocumentKeyUp, false);

        return () => {
            // Remove event listeners to handle the hot keys
            document.removeEventListener('keydown', onDocumentKeyDown);
            document.removeEventListener('keyup', onDocumentKeyUp);
        };

    }, [displayFileName]);

    const memoizedConfirmationDialog = useMemo(() => (
        <ConfirmationDialog
            title={'Editor Update'}
            open={showUpdateDialog}
            onClose={onConfirmationDialogClose}
            hideCancel
        >
            <Typography>
                The Specterr editor has been updated so that it will be easier to use as we continue to add new features.
                Don't worry, all your saved videos are unchanged and everything you could do before you can still do.
                Try it out and let us know what you think!
            </Typography>
        </ConfirmationDialog>
    ), [showUpdateDialog, onConfirmationDialogClose]);

    const memoizedPrompt = useMemo(() => (
        <Prompt
            when={videoDirty || audioDirty}
            message={'Do you want to leave this page without saving? You will lose your unsaved changes.'}
        />
    ), [videoDirty, audioDirty]);

    const memoizedJoyride = useMemo(() => (
        showWalkthrough && <Joyride onFinished={onJoyrideFinish} />
    ), [showWalkthrough, onJoyrideFinish]);

    return (
        <Container>
            <Box className={classes.main}>
                <TopBar />
                <Container disableGutters className={classes.settingsContainer}>
                    <SettingsPanel />
                </Container>
                <Container disableGutters maxWidth={false} className={classes.previewDisplayContainer}>
                    <PreviewDisplay />
                </Container>
                <BottomBarActions />
            </Box>
            <PresetDialog />
            <ExportDialog
                save={save}
                complete={onExportDialogComplete}
                seePlans={onExportDialogSeePlansClick}
            />

            {memoizedConfirmationDialog}
            {memoizedPrompt}
            {memoizedJoyride}
            {/* The component that plays the audio and calculates the frequencies for visualizer */}
            <AudioElement />
        </Container>
    );
}

const mapStateToProps = (state) => {
    const {
        videoDirty,
        audioDirty,
        exportDialogOpen,
        maxValues,
        showWalkthrough,
        showUpdateDialog,
        lyricExtractionProcessing,
        lyricExtractionJobId
    } = state.create;

    const { displayFileName } = state.create.audioSettings;
    const { id: videoSettingsId } = state.createPreview.present.videoSettings;
    const { length: presetsLength } = state.create.presets;
    const { savingInProgress } = state.create.videoSavingState;

    return {
        videoDirty,
        audioDirty,
        exportDialogOpen,
        maxValues,
        showWalkthrough,
        showUpdateDialog,
        savingInProgress,
        videoSettingsId,
        displayFileName,
        presetsLength,
        lyricExtractionProcessing,
        lyricExtractionJobId
    }
}

const mapDispatchToProps = (dispatch) => {
    const setProperty = (key, value) => dispatch(createActions.setProperty(key, value));
    const applyAudioSettingsForPresetMode = () => dispatch(createActions.applyAudioSettingsForPresetMode());
    const getVideoSettings = (videoId, callback) => dispatch(createActions.getVideoSettings(videoId, callback));
    const openExportDialog = () => dispatch(createActions.openExportDialog());
    const getPresets = (presetId) => dispatch(createActions.getPresets(presetId));
    const getMaxValues = () => dispatch(createActions.getMaxValues());
    const setShowUpdateDialog = (show) => dispatch(createActions.setShowUpdateDialog(show));
    const movePlayingPreviewForward = (duration) => dispatch(createActions.movePlayingPreviewForward(duration));
    const movePlayingPreviewBack = (duration) => dispatch(createActions.movePlayingPreviewBack(duration));
    const togglePlayingPreview = () => dispatch(createActions.togglePlayingPreview());
    const undo = () => dispatch(createActions.undo());
    const redo = () => dispatch(createActions.redo());
    const saveVideoProject = (onSaveComplete, showSnack, navigate) => dispatch(createActions.saveVideoProject(onSaveComplete, showSnack, navigate));
    const cancelLyricExtractionJob = (jobId) => dispatch(createActions.cancelLyricExtractionJob(jobId));

    return {
        setProperty,
        applyAudioSettingsForPresetMode,
        getVideoSettings,
        openExportDialog,
        getPresets,
        getMaxValues,
        setShowUpdateDialog,
        movePlayingPreviewForward,
        movePlayingPreviewBack,
        togglePlayingPreview,
        undo,
        redo,
        saveVideoProject,
        cancelLyricExtractionJob
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(memo(withStyles(CreatePage, styles)));